import {
  IconChevronRight,
  IconClock,
  IconEdit,
  IconFileCheck,
  IconUpload,
  IconUserPlus,
} from "@tabler/icons-react";
import ReusableSkeleton from "../../../../components/ReusableSkeleton/ReusableSkeleton";
import { useTaxProInfo } from "../../../../utils/hooks/useTaxProInfo";
import "./TaxProSummary.scss";
import GroupLogo from "../../../../assets/Group.png";
import type { Summary } from "../../../../utils/api/summary";
import { Avatar, Button } from "@mantine/core";
import { useState } from "react";
import SummaryModal from "./SummaryModal/SummaryModal";
import { useSearch } from "@/utils/hooks/useSearch";

const TaxProSummary = () => {
  const personalInfoString = localStorage.getItem("personalInfo");
  const personalInfo = personalInfoString ? JSON.parse(personalInfoString) : [];
  const { taxProInfo, isLoadingTaxProInfo, isErrorTaxProInfo } =
    useTaxProInfo();

  const [openModalId, setOpenModalId] = useState<number | null>(null);

  const { clients: localClients, isErrorClients: isClientsError } = useSearch();

  if (isClientsError) {
    throw new Error("Error fetching clients");
  }

  const lastUpdated = taxProInfo?.lastUpdated
    ? new Date(taxProInfo.lastUpdated).toLocaleDateString()
    : "Not available";

  if (isErrorTaxProInfo) {
    throw new Error("Error fetching tax pro info");
  }

  const eventTypeMappings: any = {
    0: {
      id: 0,
      description: "new clients signed up",
      modalTitle: "New sign-ups",
      modalDescription:
        "These are clients who have signed up while you were away",
      icon: <IconUserPlus />,
    },
    1: {
      id: 1,
      description: "clients uploaded a document",
      modalTitle: "New document uploads",
      modalDescription:
        "These are clients who have uploaded documents while you were away",
      icon: <IconUpload />,
    },
    2: {
      id: 2,
      description: "clients submitted their tax organizer",
      modalTitle: "New tax organizer submissions",
      modalDescription:
        "These are clients who have submitted their tax organizers while you were away",
      icon: <IconFileCheck />,
    },
    10: {
      id: 3,
      description: "clients updated their tax organizer",
      modalTitle: "New tax organizer updates",
      modalDescription:
        "These are clients who have updated their tax organizer while you were away",
      icon: <IconEdit />,
    },
  };

  const sortedEvents = taxProInfo?.summary
    .filter((event: Summary) => [0, 2, 10, 1].includes(event.eventType))
    .sort((a: any, b: any) => {
      const order = [0, 2, 10, 1];
      return order.indexOf(a.eventType) - order.indexOf(b.eventType);
    });

  const events =
    sortedEvents && sortedEvents.length > 0 ? (
      sortedEvents.map((event: Summary) => {
        const { description, icon, id, modalTitle, modalDescription } =
          eventTypeMappings[event.eventType as keyof typeof eventTypeMappings];
        const notificationClients = localClients?.filter((client: any) =>
          event.clientIds?.includes(client.userId)
        );

        return (
          <div key={id} className="event-item">
            <Avatar className="event-icon">{icon}</Avatar>
            <span className="event-description">
              <span className="left">
                <span className="event-count">{event.count}</span> {description}
              </span>
              <Button
                variant="transparent"
                rightSection={<IconChevronRight size={20} color={"#0077CC"} />}
                className="right"
                onClick={() => setOpenModalId(id)}
              >
                View
              </Button>
            </span>
            <SummaryModal
              show={openModalId === id}
              setShow={() => setOpenModalId(null)}
              title={modalTitle ?? ""}
              description={modalDescription ?? ""}
              clients={notificationClients ?? []}
            />
          </div>
        );
      })
    ) : (
      <p className="no-events">You have no new notifications. Great job!</p>
    );

  return (
    <ReusableSkeleton isLoading={isLoadingTaxProInfo}>
      <div className="summary-container">
        <img className="summary-logo" src={GroupLogo} alt="Wizzy Logo" />
        <h2>Hello, {personalInfo.firstName}! Wizzy's got some updates!</h2>
        <p className="summary-description">
          Wizzy’s been keeping tabs on your business. Here's a recap of what's
          happened while you were away:
        </p>
        <div className="events-container">{events}</div>
        <p className="last-updated">
          <IconClock size={20} />
          Last updated: {lastUpdated}
        </p>
      </div>
    </ReusableSkeleton>
  );
};

export default TaxProSummary;
